import classNames from 'classnames';
import * as React from 'react';
import { TableDataHeaders, TableHeaderOptions } from 'types/data-tables/column-by-key.js';
import { getDefaultCellClassNames } from '../rows/row-classes-default.js';
import { CoreCellProps, ICellLookup, ICellTuples, assertUnreachable, getDefaultComponents } from '../types.js';

type RenderCellProps = {
  data: TableDataHeaders | undefined;
  overrides?: Partial<ComponentTableTypes>;
} & CoreCellProps;

export type ComponentTester<T extends CellRenderType> = React.ComponentType<{
  data?: TableHeaderOptions<ICellLookup<'Render'>[T]>;
  classes?: string;
}>;

export type ComponentTableTypes = {
  [P in CellRenderType]: ComponentTester<P>;
};

export enum CellRenderType {
  date = 'date',
  dateString = 'dateString',
  renderString = 'renderString',
  stringBold = 'boldString',
  statusRating = 'statusRating',
  rainfall = 'rainfall',
  windchill = 'windChill',
  link = 'link',
}

function getComponentsFactory(overrides: Partial<ComponentTableTypes> = {}) {
  const defaultComponents: ComponentTableTypes = getDefaultComponents();
  const result: ComponentTableTypes = { ...defaultComponents, ...overrides };
  const getComponent = <T extends CellRenderType>(acc: T): ComponentTableTypes[T] => result[acc];
  return getComponent;
}

export function RenderCell({ data, cellInfo, getCellClassNames, overrides }: Omit<RenderCellProps, 'title'>) {
  if (data) {
    const { vals, render } = data;
    const value = vals[cellInfo.index];
    const newTypes = [render, value] as ICellTuples<'Render'>; // we hack the types here
    const [acc, val] = newTypes;
    const getComponent = getComponentsFactory(overrides);

    const getData = (): JSX.Element => {
      switch (acc) {
        case CellRenderType.dateString: {
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        case CellRenderType.date: {
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        case CellRenderType.rainfall: {
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        case CellRenderType.windchill: {
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        case CellRenderType.statusRating: {
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        case CellRenderType.link: {
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        case CellRenderType.renderString: {
          // data e.g. { period: '9sec' }
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        case CellRenderType.stringBold: {
          // data e.g. { period: '9sec' }
          const Result = getComponent(acc);
          return <Result data={val} />;
        }
        default:
          assertUnreachable(acc);
      }
    };
    const className = getCellClassNames(cellInfo);
    const colInfo = getDefaultCellClassNames(cellInfo);

    return <td className={classNames(colInfo, className)}>{getData()}</td>;
  }
  return null;
}
